import { useMutation, useQueryClient } from '@tanstack/react-query';
import { movimentacaoService } from '../../services/movimentacaoService';
import type { Movimentacao, MovimentacaoCreate, MovimentacaoExclusao, MovimentacaoUpdate } from '../../models/movimentacao.types';
import { SI_MOVIMENTACOES_QUERY_KEYS } from './useMovimentacoesQuery';
import { SI_SOLICITACOES_QUERY_KEYS } from './useSolicitacoesQuery';
import { SI_EQUIPAMENTOS_QUERY_KEYS } from './useEquipamentoQuery';
import { useToast } from '@/hooks/useToast';

export function useMovimentacaoMutations(solicitacaoId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidar = () => {
    queryClient.invalidateQueries({
      queryKey: SI_MOVIMENTACOES_QUERY_KEYS.porSolicitacao(solicitacaoId),
    });
    // Uma Movimentação concluída pode mudar o status agregado da Solicitação
    // (via Cloud Function concluirSolicitacao) — invalida a fila/detalhe também.
    queryClient.invalidateQueries({ queryKey: SI_SOLICITACOES_QUERY_KEYS.all });
  };

  const criarMovimentacao = useMutation({
    mutationFn: (data: MovimentacaoCreate) => movimentacaoService.create(solicitacaoId, data),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao criar movimentação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const atualizarMovimentacao = useMutation({
    mutationFn: (data: MovimentacaoUpdate) => movimentacaoService.update(solicitacaoId, data),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao atualizar movimentação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const moverEquipamentoParaNovaMovimentacao = useMutation({
    mutationFn: ({ movimentacaoOrigemId, itemId }: { movimentacaoOrigemId: string; itemId: string }) =>
      movimentacaoService.moverEquipamentoParaNovaMovimentacao(solicitacaoId, movimentacaoOrigemId, itemId),
    onSuccess: () => {
      invalidar();
      toast.success('Equipamento movido para uma nova Movimentação.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao mover equipamento: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const atualizarFotosEquipamento = useMutation({
    mutationFn: ({
      movimentacaoId,
      itemId,
      alterarFotos,
    }: {
      movimentacaoId: string;
      itemId: string;
      alterarFotos: (fotosAtuais: string[]) => string[];
    }) => movimentacaoService.atualizarFotosEquipamento(solicitacaoId, movimentacaoId, itemId, alterarFotos),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao atualizar fotos: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const excluirMovimentacao = useMutation({
    mutationFn: (data: MovimentacaoExclusao) => movimentacaoService.excluir(solicitacaoId, data),
    onSuccess: () => {
      invalidar();
      toast.success('Movimentação excluída.');
    },
    onError: (error: any) => {
      toast.error(`Erro ao excluir movimentação: ${error?.message || 'erro desconhecido'}`);
    },
  });

  const anexarTermoAssinado = useMutation({
    mutationFn: ({
      movimentacao,
      arquivo,
      autor,
      dono,
    }: {
      movimentacao: Pick<Movimentacao, 'id' | 'equipamentosExecucao'>;
      arquivo: File;
      autor: { id: string; name: string };
      dono: { id: string; name: string } | null;
    }) => movimentacaoService.anexarTermoAssinado(solicitacaoId, movimentacao, arquivo, autor, dono),
    onSuccess: () => {
      toast.success('Termo assinado anexado — já está na ficha do equipamento.');
      invalidar();
      queryClient.invalidateQueries({ queryKey: SI_EQUIPAMENTOS_QUERY_KEYS.all });
    },
    onError: (error: any) => {
      toast.error(`Erro ao anexar o termo assinado: ${error?.message || 'erro desconhecido'}`);
    },
  });

  return {
    anexarTermoAssinado,
    criarMovimentacao,
    excluirMovimentacao,
    atualizarMovimentacao,
    moverEquipamentoParaNovaMovimentacao,
    atualizarFotosEquipamento,
  };
}
